import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNotifications } from '../../../components/notifications';
import LoadingSpinner from '../../LoadingSpinner';
import { crewsAPI, autoplannerAPI } from '../../../services/api';
import CrewSeatingSetup from './CrewSeatingSetup';

// Small helper component to keep top navigation state synced
const RegisterNavUpdater = ({ registerNav, onNext, onBack, canNext, canBack }) => {
  const prevRef = useRef(null);
  const onNextRef = useRef(onNext);
  const onBackRef = useRef(onBack);

  // Update refs when props change
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    const payload = {
      onNext: onNextRef.current,
      onBack: onBackRef.current,
      canNext,
      canBack,
    };
    const prev = prevRef.current;
    const changed =
      !prev ||
      prev.onNext !== payload.onNext ||
      prev.onBack !== payload.onBack ||
      prev.canNext !== payload.canNext ||
      prev.canBack !== payload.canBack;
    if (changed) {
      registerNav(payload);
      prevRef.current = payload;
    }
  }, [registerNav, canNext, canBack]);
  return null;
};

const Step4LockedSeatings = ({
  trainingGroupId,
  selectedSessions,
  lockedSeatings,
  onComplete,
  onBack,
  taskId,
  registerNav,
}) => {
  const { showToast } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [crews, setCrews] = useState([]);
  const [crewSetups, setCrewSetups] = useState({});
  const [loadingSetups, setLoadingSetups] = useState({});
  const [validationStatus, setValidationStatus] = useState({
    hasConflicts: false,
    conflicts: [],
    totalLocked: 0,
    totalVariable: 0,
  });
  const [hasValidatedInitialData, setHasValidatedInitialData] = useState(false);
  const [shouldSave, setShouldSave] = useState(false);
  const [normalizedData, setNormalizedData] = useState(null);
  const [loadingNormalizedData, setLoadingNormalizedData] = useState(false);

  // Use refs to store latest values to avoid infinite re-renders
  const onCompleteRef = useRef(onComplete);
  const onBackRef = useRef(onBack);

  // Update refs when props change
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  // Quick lookup for session metadata by id for nicer conflict UX
  const sessionById = useMemo(() => {
    const map = {};
    (selectedSessions || []).forEach((s) => {
      if (s && s.id !== undefined && s.id !== null) map[s.id] = s;
    });
    return map;
  }, [selectedSessions]);

  const selectedSessionIds = useMemo(() => {
    return (selectedSessions || [])
      .map((s) => (s && s.id !== undefined && s.id !== null ? s.id : null))
      .filter((id) => id !== null);
  }, [selectedSessions]);

  // Stable dependency key to avoid re-running effects due to array identity changes
  const selectedSessionIdsKey = useMemo(() => {
    const sorted = [...selectedSessionIds].sort((a, b) => Number(a) - Number(b));
    return sorted.join(',');
  }, [selectedSessionIds]);

  // Define before any hook dependencies that reference it
  const prepareDataForSave = useCallback(() => {
    const lockedSeatingsData = {};
    console.log('prepareDataForSave - crews:', crews); // Debug log
    console.log('prepareDataForSave - crewSetups:', crewSetups); // Debug log

    crews.forEach((crew) => {
      const crewSetupsForCrew = crewSetups[crew.id] || [];
      console.log(`prepareDataForSave - crew ${crew.id} setups:`, crewSetupsForCrew); // Debug log
      // Save all setups, even empty ones, so they can be loaded back
      if (crewSetupsForCrew.length > 0) {
        lockedSeatingsData[crew.id] = {
          setups: crewSetupsForCrew.map((setup) => ({
            id: setup.id,
            name: setup.name,
            sessions: setup.sessions,
            seating: setup.seating,
            availability: setup.availability,
          })),
        };
      }
    });
    console.log('prepareDataForSave - result:', lockedSeatingsData); // Debug log
    return lockedSeatingsData;
  }, [crews, crewSetups]);

  const saveLockedSeatings = async (data) => {
    if (!taskId) return;

    try {
      setSaving(true);
      await autoplannerAPI.saveLockedSeatings(taskId, data);

      // Validate after saving
      await validateLockedSeatings(data);
    } catch (error) {
      console.error('Error saving locked seatings:', error);
      showToast('Error saving locked seatings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const loadSetupsForCrew = async (crew) => {
    setLoadingSetups((prev) => ({ ...prev, [crew.id]: true }));

    try {
      if (taskId) {
        // Load from backend
        const result = await autoplannerAPI.getLockedSeatings(taskId);
        if (result.success && result.locked_seatings) {
          const convertedSetups = convertLockedSeatingsToSetups(result.locked_seatings, crew.id);
          setCrewSetups((prev) => ({ ...prev, [crew.id]: convertedSetups }));
        } else {
          setCrewSetups((prev) => ({ ...prev, [crew.id]: [] }));
        }
      } else if (lockedSeatings) {
        // Load from props (fallback)
        const convertedSetups = convertLockedSeatingsToSetups(lockedSeatings, crew.id);
        setCrewSetups((prev) => ({ ...prev, [crew.id]: convertedSetups }));
      } else {
        setCrewSetups((prev) => ({ ...prev, [crew.id]: [] }));
      }
    } catch (error) {
      console.error('Error loading setups for crew:', error);
      setCrewSetups((prev) => ({ ...prev, [crew.id]: [] }));
    } finally {
      setLoadingSetups((prev) => ({ ...prev, [crew.id]: false }));
    }
  };

  const loadNormalizedData = async () => {
    if (!taskId) return;

    try {
      setLoadingNormalizedData(true);
      const result = await autoplannerAPI.getNormalizedDataset(taskId);
      if (result.success && result.normalized_data?.success) {
        setNormalizedData(result.normalized_data.normalized_data);
      }
    } catch (error) {
      console.error('Error loading normalized dataset:', error);
    } finally {
      setLoadingNormalizedData(false);
    }
  };

  // Extract excluded rowers per session from normalized data
  const excludedRowersPerSession = normalizedData?.display_data?.excluded_rowers_per_session || {};

  const isRowerExcludedForSession = (rowerId, sessionId) => {
    const sessionExcludedRowers = excludedRowersPerSession[sessionId] || [];
    return sessionExcludedRowers.some((exclusion) => exclusion.user_id === rowerId);
  };

  const validateLockedSeatings = async (data) => {
    if (!taskId) return;

    try {
      setValidating(true);
      console.log('Validating data:', data); // Debug log
      const validationResult = await autoplannerAPI.validateLockedSeatings(taskId, data);

      if (validationResult.success) {
        // Add additional validation for excluded rowers
        const additionalConflicts = [];

        // Check each crew setup for excluded rowers
        Object.entries(data).forEach(([crewId, crewData]) => {
          if (crewData.setups) {
            crewData.setups.forEach((setup) => {
              if (setup.sessions && setup.seating) {
                setup.sessions.forEach((sessionId) => {
                  Object.entries(setup.seating).forEach(([seatNumber, rowerId]) => {
                    if (isRowerExcludedForSession(rowerId, sessionId)) {
                      const crew = crews.find((c) => c.id === parseInt(crewId));
                      const crewName = crew ? crew.name : `Crew ${crewId}`;
                      const session = selectedSessions.find((s) => s.id === sessionId);
                      const sessionTitle = session ? session.title : `Session ${sessionId}`;

                      additionalConflicts.push({
                        type: 'excluded_rower_seated',
                        message: `Rower ${rowerId} is excluded for session "${sessionTitle}" but is seated in ${crewName} at seat ${seatNumber}`,
                        crew_id: parseInt(crewId),
                        crew_name: crewName,
                        session_id: sessionId,
                        session_title: sessionTitle,
                        rower_id: rowerId,
                        seat_number: seatNumber,
                      });
                    }
                  });
                });
              }
            });
          }
        });

        const allConflicts = [...(validationResult.conflicts || []), ...additionalConflicts];

        setValidationStatus({
          hasConflicts: allConflicts.length > 0,
          conflicts: allConflicts,
          totalLocked: validationResult.total_locked || 0,
          totalVariable: validationResult.total_variable || 0,
        });
      } else {
        console.error('Validation failed:', validationResult.error);
        showToast('Error validating locked seatings', 'error');
      }
    } catch (error) {
      console.error('Error validating locked seatings:', error);
      showToast('Error validating locked seatings', 'error');
    } finally {
      setValidating(false);
    }
  };

  // Watch for crewSetups changes and trigger save/validation
  useEffect(() => {
    if (shouldSave && crews.length > 0 && Object.keys(crewSetups).length > 0) {
      const dataToSave = prepareDataForSave();
      console.log('Saving data:', dataToSave); // Debug log
      saveLockedSeatings(dataToSave);
      setShouldSave(false);
    }
  }, [crewSetups, shouldSave, crews, prepareDataForSave]);

  const triggerSave = () => {
    setShouldSave(true);
  };

  const convertLockedSeatingsToSetups = (lockedSeatings, crewId) => {
    if (!lockedSeatings) return [];

    // Handle the new backend data structure
    if (lockedSeatings[crewId]) {
      const crewData = lockedSeatings[crewId];
      if (crewData.setups) {
        return crewData.setups.map((setup, index) => ({
          id: setup.id || Date.now() + index,
          name: setup.name || `Setup ${index + 1}`,
          sessions: setup.sessions || [],
          seating: setup.seating || {},
          availability: setup.availability || {},
        }));
      }
    }

    // Fallback for old data structure
    if (lockedSeatings.setups) {
      return lockedSeatings.setups.map((setup, index) => ({
        id: setup.id || Date.now() + index,
        name: setup.name || `Setup ${index + 1}`,
        sessions: setup.sessions || [],
        seating: setup.seating || {},
        availability: setup.availability || {},
      }));
    }

    if (lockedSeatings.crewId === crewId && lockedSeatings.setups) {
      return lockedSeatings.setups.map((setup, index) => ({
        id: setup.id || Date.now() + index,
        name: setup.name || `Setup ${index + 1}`,
        sessions: setup.sessions || [],
        seating: setup.seating || {},
        availability: setup.availability || {},
      }));
    }
    return [];
  };

  useEffect(() => {
    const loadCrews = async () => {
      try {
        setLoading(true);
        const crewsData = await crewsAPI.getCrews({
          experimental_only: true,
          training_group_id: trainingGroupId,
        });

        let filteredCrews = crewsData;
        if (taskId && selectedSessionIds.length > 0) {
          try {
            const task = await autoplannerAPI.getAutoplannerTask(taskId);
            let exclusions = {};
            const raw = task?.session_exclusions;
            if (raw) {
              exclusions = typeof raw === 'string' ? JSON.parse(raw) : raw;
            }
            filteredCrews = crewsData.filter((crew) => {
              const crewKey = String(crew.id);
              const excludedForCrew = new Set(
                (exclusions?.[crewKey] || []).filter((x) => x !== null && x !== undefined)
              );
              if (excludedForCrew.size === 0) return true;
              // A crew is fully excluded if all selected sessions are excluded
              const allExcluded = selectedSessionIds.every((sid) => excludedForCrew.has(sid));
              return !allExcluded;
            });
          } catch (e) {
            console.error('Error determining fully excluded crews:', e);
            // If something goes wrong, fall back to showing all crews
            filteredCrews = crewsData;
          }
        }

        setCrews(filteredCrews);
        const initialCrewSetups = {};
        const initialLoadingSetups = {};
        filteredCrews.forEach((crew) => {
          initialCrewSetups[crew.id] = [];
          initialLoadingSetups[crew.id] = false;
        });
        setCrewSetups(initialCrewSetups);
        setLoadingSetups(initialLoadingSetups);
        filteredCrews.forEach((crew) => loadSetupsForCrew(crew));
        loadNormalizedData(); // Load normalized data after crews and setups are loaded
      } catch (error) {
        console.error('Error loading crews:', error);
        showToast('Error loading crews', 'error');
      } finally {
        setLoading(false);
      }
    };
    if (trainingGroupId) loadCrews();
  }, [trainingGroupId, showToast, taskId, selectedSessionIdsKey]);

  // Validate loaded data after all crews are loaded
  useEffect(() => {
    const validateLoadedData = async () => {
      if (
        crews.length > 0 &&
        !loading &&
        Object.keys(crewSetups).length > 0 &&
        !hasValidatedInitialData
      ) {
        // Check if all crews have finished loading
        const allCrewsLoaded = crews.every((crew) => !loadingSetups[crew.id]);
        if (allCrewsLoaded) {
          const dataToValidate = prepareDataForSave();
          if (Object.keys(dataToValidate).length > 0) {
            await validateLockedSeatings(dataToValidate);
            setHasValidatedInitialData(true);
          }
        }
      }
    };

    validateLoadedData();
  }, [crews, loading, crewSetups, loadingSetups, hasValidatedInitialData, prepareDataForSave]);

  const getCrewCapacity = (boatType) => {
    if (!boatType) return 0;
    const boatTypeLower = boatType.toLowerCase();
    if (boatTypeLower.includes('8')) return 8;
    if (boatTypeLower.includes('4')) return 4;
    if (boatTypeLower.includes('2')) return 2;
    if (boatTypeLower.includes('1')) return 1;
    return 0;
  };

  const getSeatPositions = (boatType) => {
    const capacity = getCrewCapacity(boatType);
    const positions = Array.from({ length: capacity }, (_, i) => i + 1);
    if (boatType?.includes('+') || boatType?.includes('*')) {
      positions.push(0);
    }
    return positions;
  };

  const handleAddSetup = (crewId) => {
    const newSetup = {
      id: Date.now(),
      name: `Setup ${crewSetups[crewId]?.length + 1}`,
      sessions: [],
      seating: {},
      availability: {},
    };
    const updatedSetups = [...(crewSetups[crewId] || []), newSetup];
    setCrewSetups((prev) => ({ ...prev, [crewId]: updatedSetups }));

    // Save
    triggerSave();
  };

  const handleSetupNameChange = (crewId, setupId, newName) => {
    const updatedSetups = crewSetups[crewId].map((setup) =>
      setup.id === setupId ? { ...setup, name: newName } : setup
    );
    setCrewSetups((prev) => ({ ...prev, [crewId]: updatedSetups }));

    // Save
    triggerSave();
  };

  const handleDeleteSetup = (crewId, setupId) => {
    const updatedSetups = crewSetups[crewId].filter((setup) => setup.id !== setupId);
    setCrewSetups((prev) => ({ ...prev, [crewId]: updatedSetups }));

    // Save
    triggerSave();
  };

  const handleSessionAssignment = (crewId, setupId, sessionId, isAssigned) => {
    const updatedSetups = crewSetups[crewId].map((setup) => {
      if (setup.id === setupId) {
        const updatedSessions = isAssigned
          ? [...setup.sessions, sessionId]
          : setup.sessions.filter((id) => id !== sessionId);
        return { ...setup, sessions: updatedSessions };
      }
      // Remove session from other setups if it's being assigned to this setup
      if (isAssigned) {
        const updatedSessions = setup.sessions.filter((id) => id !== sessionId);
        return { ...setup, sessions: updatedSessions };
      }
      return setup;
    });
    setCrewSetups((prev) => ({ ...prev, [crewId]: updatedSetups }));

    // Save
    triggerSave();
  };

  const handleRowerAssignment = async (crewId, setupId, seatNumber, userId) => {
    const updatedSetups = crewSetups[crewId].map((setup) => {
      if (setup.id === setupId) {
        const updatedSeating = { ...setup.seating };
        if (userId) updatedSeating[seatNumber] = userId;
        else delete updatedSeating[seatNumber];
        return { ...setup, seating: updatedSeating };
      }
      return setup;
    });
    setCrewSetups((prev) => ({ ...prev, [crewId]: updatedSetups }));

    // Save
    triggerSave();
  };

  const handleComplete = () => {
    const dataToSave = prepareDataForSave();
    onCompleteRef.current(dataToSave);
  };

  const navOnNext = useCallback(() => handleComplete(), []);
  const navOnBack = useCallback(() => onBackRef.current(), []);
  const navCanNext = !validationStatus.hasConflicts;

  // Keep top navigation in sync without causing render loops
  useEffect(() => {
    if (!registerNav) return;
    // Use RegisterNavUpdater component to handle navigation registration
  }, [registerNav]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mx-auto max-w-6xl pb-28 md:pb-20">
      {registerNav && (
        <RegisterNavUpdater
          registerNav={registerNav}
          onNext={navOnNext}
          onBack={navOnBack}
          canNext={navCanNext}
          canBack={true}
        />
      )}
      <div className="rounded-lg bg-white p-4 shadow sm:p-6">
        <div className="mb-6">
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Step 4: Lock Seating Positions</h2>
          <p className="text-gray-600">
            Assign rowers to specific positions in crews for selected sessions.
          </p>
          <div className="mt-2 flex h-6 items-center space-x-4">
            {saving && (
              <div className="flex items-center text-sm text-blue-600">
                <div className="mr-2 h-3 w-3 animate-spin rounded-full border-b-2 border-blue-600"></div>
                Saving changes...
              </div>
            )}
            {validating && (
              <div className="flex items-center text-sm text-orange-600">
                <div className="mr-2 h-3 w-3 animate-spin rounded-full border-b-2 border-orange-600"></div>
                Validating...
              </div>
            )}
          </div>
        </div>

        {/* Build a quick lookup for session titles */}
        {/** Inline helpers kept small and pure for clarity **/}
        {(() => {
          const ConflictBadge = ({ type }) => {
            const map = {
              rower_multiple_setups_same_session: {
                label: 'Same session in multiple setups',
                color: 'bg-amber-100 text-amber-800 border-amber-200',
                icon: '⚠️',
              },
              multiple_seats_same_setup: {
                label: 'Multiple seats in same setup',
                color: 'bg-rose-100 text-rose-800 border-rose-200',
                icon: '⛔',
              },
              excluded_rower_seated: {
                label: 'Excluded rower seated',
                color: 'bg-red-100 text-red-800 border-red-200',
                icon: '🚫',
              },
            };
            const cfg = map[type] || {
              label: type,
              color: 'bg-gray-100 text-gray-800 border-gray-200',
              icon: '•',
            };
            return (
              <span
                className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${cfg.color}`}
              >
                <span className="mr-1">{cfg.icon}</span>
                {cfg.label}
              </span>
            );
          };

          const ConflictCard = ({ conflict }) => {
            const type = conflict?.type;
            if (type === 'rower_multiple_setups_same_session') {
              const s = sessionById[conflict.session_id];
              const title = s?.title || `Session #${conflict.session_id}`;
              const rows = (conflict.assignments || []).map((a, i) => (
                <tr key={i} className="text-sm text-gray-700">
                  <td className="whitespace-nowrap py-1 pr-3">{a.setup_name}</td>
                  <td className="whitespace-nowrap py-1 pr-3">{a.crew_name}</td>
                  <td className="whitespace-nowrap py-1 pr-3">{a.seat}</td>
                </tr>
              ));
              return (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <ConflictBadge type={type} />
                        <span className="font-semibold text-red-900">
                          {conflict.rower_name} appears in multiple setups for session: {title}
                        </span>
                      </div>
                      {s?.date && <div className="mt-1 text-xs text-red-700">{s.date}</div>}
                    </div>
                  </div>
                  <div className="overflow-auto">
                    <table className="w-full min-w-[400px] rounded border bg-white">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-600">
                          <th className="px-3 py-2 text-left font-medium">Setup</th>
                          <th className="px-3 py-2 text-left font-medium">Crew</th>
                          <th className="px-3 py-2 text-left font-medium">Seat</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">{rows}</tbody>
                    </table>
                  </div>
                </div>
              );
            }

            if (type === 'multiple_seats_same_setup') {
              const seats = (conflict.seats || []).sort((a, b) => Number(a) - Number(b));
              return (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="mb-2 flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <ConflictBadge type={type} />
                      <span className="font-semibold text-red-900">
                        {conflict.rower_name} has multiple seats in {conflict.setup_name} (
                        {conflict.crew_name})
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-red-800">Seats: {seats.join(', ')}</div>
                </div>
              );
            }

            if (type === 'excluded_rower_seated') {
              return (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="mb-2 flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <ConflictBadge type={type} />
                      <span className="font-semibold text-red-900">
                        Excluded rower seated in session
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-red-800">
                    <p>
                      <strong>Rower ID:</strong> {conflict.rower_id}
                    </p>
                    <p>
                      <strong>Crew:</strong> {conflict.crew_name}
                    </p>
                    <p>
                      <strong>Session:</strong> {conflict.session_title}
                    </p>
                    <p>
                      <strong>Seat:</strong> {conflict.seat_number}
                    </p>
                    <p className="mt-2 text-xs text-red-700">
                      This rower was excluded from this session through the resolution interface but
                      is still assigned to a seat.
                    </p>
                  </div>
                </div>
              );
            }

            // Fallback rendering using message
            return (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <ConflictBadge type={conflict?.type} />
                  <span className="font-semibold text-red-900">Conflict</span>
                </div>
                <div className="whitespace-pre-line rounded border bg-red-100 p-3 font-mono text-xs">
                  {conflict.message}
                </div>
              </div>
            );
          };

          return (
            validationStatus.hasConflicts && (
              <div className="mb-6">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-red-800">Conflicts Detected</h3>
                  <span className="text-sm text-red-700">
                    {validationStatus.conflicts.length} issue(s)
                  </span>
                </div>
                <div className="space-y-4">
                  {(validationStatus.conflicts || []).map((c, idx) => (
                    <ConflictCard key={idx} conflict={c} />
                  ))}
                </div>
              </div>
            )
          );
        })()}

        {crews.map((crew) => (
          <div key={crew.id} className="mb-8">
            <h3 className="mb-3 text-xl font-bold text-gray-900">
              {crew.name} ({crew.boat_type})
            </h3>
            {loadingSetups[crew.id] ? (
              <div className="flex items-center justify-center py-4">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <span>Loading saved setups...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {crewSetups[crew.id]?.map((setup) => (
                  <CrewSeatingSetup
                    key={setup.id}
                    crew={crew}
                    setup={setup}
                    selectedSessions={selectedSessions}
                    onSetupNameChange={handleSetupNameChange}
                    onDeleteSetup={handleDeleteSetup}
                    onSessionAssignment={handleSessionAssignment}
                    onRowerAssignment={handleRowerAssignment}
                    getSeatPositions={getSeatPositions}
                  />
                ))}
                <button
                  onClick={() => handleAddSetup(crew.id)}
                  className="w-full rounded-lg border-2 border-dashed border-gray-300 bg-white px-4 py-3 text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-700"
                >
                  + Add New Setup
                </button>
              </div>
            )}
          </div>
        ))}

        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white p-3 shadow-lg sm:p-4">
          <div className="mx-auto max-w-6xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  {validationStatus.totalLocked} positions locked, {validationStatus.totalVariable}{' '}
                  variable
                  {crews.length === 0 && ' (no crews loaded)'}
                </div>
                {validationStatus.hasConflicts && (
                  <div className="flex items-center text-sm text-red-600">
                    <div className="mr-1 h-2 w-2 rounded-full bg-red-500"></div>
                    {validationStatus.conflicts.length} conflict(s) detected
                  </div>
                )}
              </div>
              <div className="hidden space-x-3 md:flex">
                <button
                  onClick={() => onBackRef.current()}
                  className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleComplete}
                  disabled={validationStatus.hasConflicts}
                  className="rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continue
                </button>
              </div>
              <div className="flex space-x-3 md:hidden">
                <button
                  onClick={() => onBackRef.current()}
                  className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleComplete}
                  disabled={validationStatus.hasConflicts}
                  className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step4LockedSeatings;
